import { applyParams } from "../../../params";
import { makePath } from "../../../url";
import * as APITypes from "../../generated-types/v2/Api";
import { applyPagination } from "../../pagination";
import { deleteJSON, getJSON, putJSON } from "../../request";
import { APIContext } from "../../types";

export type QuestionaireAPI = ReturnType<typeof createQuestionnaireAPI>;

type QueryParams = Partial<APITypes.QuestionnaireQueryPatientResponsesParams>;

function applyOptions(params: URLSearchParams, options: QueryParams) {
  applyParams(params, {
    visitId: options.visitId,
    questionnaireType: options.questionnaireType,
    "createdAtRange.start": options["createdAtRange.start"],
    "createdAtRange.end": options["createdAtRange.end"],
  });
}

export function createQuestionnaireAPI({
  buildURL,
  fetchJSON,
  fetchPagedJSON,
  fetchRaw,
}: APIContext) {
  function getQuestionnaireResponses(
    { patientId }: { patientId: string },
    params: Omit<APITypes.QuestionnaireQueryPatientResponsesParams, "patientId">
  ) {
    const path = makePath(["api", "v2", "patient", patientId, "questionnaire"]);
    const url = buildURL(path);
    applyOptions(url.searchParams, params);
    applyPagination(url.searchParams, params);

    const request = getJSON(url);
    return fetchPagedJSON<APITypes.QuestionnaireResponse>(request);
  }

  function getQuestionnaireResponse({
    patientId,
    responseId,
  }: {
    patientId: string;
    responseId: string;
  }) {
    const path = makePath([
      "api",
      "v2",
      "patient",
      patientId,
      "questionnaire",
      responseId,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchPagedJSON<APITypes.QuestionnaireResponse>(request);
  }

  function deleteQuestionnaireResponse({
    patientId,
    responseId,
  }: {
    patientId: string;
    responseId: string;
  }) {
    const path = makePath([
      "api",
      "v2",
      "patient",
      patientId,
      "questionnaire",
      responseId,
    ]);
    const url = buildURL(path);
    const request = deleteJSON(url, undefined);

    return fetchRaw(request);
  }

  function createOrUpdateQuestionnaireResponse(
    {
      patientId,
      responseId,
    }: {
      patientId: string;
      responseId: string;
    },
    data: APITypes.QuestionnaireResponse
  ) {
    const path = makePath([
      "api",
      "v2",
      "patient",
      patientId,
      "questionnaire",
      responseId,
    ]);
    const url = buildURL(path);
    const request = putJSON(url, data);
    return fetchJSON<APITypes.QuestionnaireResponse>(request);
  }

  return {
    getQuestionnaireResponses,
    getQuestionnaireResponse,
    deleteQuestionnaireResponse,
    createOrUpdateQuestionnaireResponse,
  };
}
